import { useCallback, useEffect, useRef } from 'react';

export default function usePdfSectionNavigation({
  containerRef,
  numPages,
  sections,
  pageScale,
  zoomLevel,
  isProgrammaticScrollRef,
  autoScrollRequestedRef,
  currentSectionIndex,
  setCurrentSectionIndex,
  setCurrentPage,
  findAndScrollToSection,
}) {
  const sectionAnchorsRef = useRef([]);

  const goToSection = useCallback((sectionIndex) => {
    if (sectionIndex >= 0 && sectionIndex < sections.length) {
      const section = sections[sectionIndex];

      setCurrentSectionIndex(sectionIndex);
      const targetPage = section.pageNumber || 1;
      autoScrollRequestedRef.current = true;
      setCurrentPage(targetPage);

      setTimeout(() => {
        let pageElement = document.querySelector(`[data-page-number="${targetPage}"]`);
        let found = false;

        if (pageElement) {
          const textLayer = pageElement.querySelector('.react-pdf__Page__textContent');
          if (textLayer) {
            const allText = textLayer.textContent || '';
            const sectionNumber = section.sectionNumber || '';
            if (allText.includes(sectionNumber)) {
              found = true;
              pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
              setTimeout(() => {
                findAndScrollToSection(textLayer, section, pageElement);
              }, 200);
            }
          }
        }

        if (!found) {
          const allPages = document.querySelectorAll('[data-page-number]');

          for (const pageEl of allPages) {
            const pageNum = parseInt(pageEl.getAttribute('data-page-number') || '0', 10);
            const textLayer = pageEl.querySelector('.react-pdf__Page__textContent');

            if (textLayer) {
              const allText = textLayer.textContent || '';
              const sectionNumber = section.sectionNumber || '';

              if (allText.includes(sectionNumber)) {
                pageElement = pageEl;
                found = true;
                setCurrentPage(pageNum);
                pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setTimeout(() => {
                  findAndScrollToSection(textLayer, section, pageEl);
                }, 200);
                break;
              }
            }
          }
        }

        if (!found) {
          if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            const firstPage = document.querySelector('[data-page-number="1"]');
            if (firstPage) {
              firstPage.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }
      }, 100);
    }
  }, [sections, setCurrentSectionIndex, autoScrollRequestedRef, setCurrentPage, findAndScrollToSection]);

  const goToNextSection = useCallback(() => {
    if (currentSectionIndex < sections.length - 1) {
      goToSection(currentSectionIndex + 1);
    }
  }, [currentSectionIndex, sections.length, goToSection]);

  const goToPreviousSection = useCallback(() => {
    if (currentSectionIndex > 0) {
      goToSection(currentSectionIndex - 1);
    }
  }, [currentSectionIndex, goToSection]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!sections || sections.length === 0) {
      sectionAnchorsRef.current = [];
      return;
    }

    let timeoutId;
    let observer;

    const buildAnchors = () => {
      const nextAnchors = [];
      const containerRect = container.getBoundingClientRect();

      const stripLeadingSectionNumber = (value) => {
        if (!value || typeof value !== 'string') return '';
        return value
          .replace(/^\s*(?:section\s*)?\d+(?:\.\d+)*\s*[:\-–—.)]\s*/i, '')
          .replace(/^\s*(?:section\s*)?\d+(?:\.\d+)*\s+(?=[A-Za-z(])/i, '')
          .replace(/^\s*\d+(?:\.\d+)*\s*[:\-–—.)]\s*/i, '')
          .replace(/^\s*\d+(?:\.\d+)*\s+(?=[A-Za-z(])/i, '')
          .trim();
      };

      const findAnchorForSection = (section) => {
        const pageNum = section?.pageNumber ?? section?.page_number ?? 1;
        const pageElement = container.querySelector(`[data-page-number="${pageNum}"]`);
        if (!pageElement) return null;

        const textLayer = pageElement.querySelector('.react-pdf__Page__textContent');
        if (!textLayer) return null;

        const sectionNumber = String(section?.sectionNumber || '').trim();
        const rawTitle = String(section?.fullTitle || section?.title || '').trim();
        const sectionTitle = stripLeadingSectionNumber(rawTitle);

        const normalizeHeadingKey = (value) => {
          if (!value || typeof value !== 'string') return '';
          return value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '');
        };

        const sectionTitleKey = normalizeHeadingKey(sectionTitle);
        const sectionNumberKey = normalizeHeadingKey(sectionNumber);

        const spans = textLayer.querySelectorAll('span');
        const reStart = sectionNumber
          ? new RegExp(`^\\s*${sectionNumber.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}\\b`)
          : null;

        let best = null;
        const spanList = Array.from(spans);
        for (let i = 0; i < spanList.length; i += 1) {
          const span = spanList[i];
          const text = (span.textContent || '').trim();
          if (!text) continue;

          if (reStart && (reStart.test(text) || (sectionNumber && text.includes(sectionNumber)))) {
            best = span;
            if (reStart.test(text)) break;
            continue;
          }

          if (sectionTitleKey) {
            const textKey = normalizeHeadingKey(text);
            if (textKey && (textKey === sectionTitleKey || textKey.includes(sectionTitleKey))) {
              best = span;
              break;
            }

            let combined = '';
            for (let j = i; j < Math.min(i + 10, spanList.length); j += 1) {
              combined += (spanList[j].textContent || '').trim();
              const combinedKey = normalizeHeadingKey(combined);
              if (combinedKey && combinedKey.includes(sectionTitleKey)) {
                best = span;
                break;
              }
              if (combined.length > (sectionTitle.length || 0) * 3 + 30) break;
            }
            if (best) break;
          }

          if (sectionNumberKey) {
            const textKey = normalizeHeadingKey(text);
            if (textKey && textKey.includes(sectionNumberKey)) {
              best = span;
              break;
            }
          }
        }

        if (!best) return null;
        const rect = best.getBoundingClientRect();
        const top = rect.top - containerRect.top + container.scrollTop;
        return Number.isFinite(top) ? top : null;
      };

      sections.forEach((section, index) => {
        const anchorTop = findAnchorForSection(section);
        if (anchorTop != null) {
          nextAnchors.push({ sectionIndex: index, top: anchorTop });
          return;
        }

        const pageNum = section?.pageNumber || 1;
        const pageElement = container.querySelector(`[data-page-number="${pageNum}"]`);
        if (pageElement) {
          nextAnchors.push({ sectionIndex: index, top: pageElement.offsetTop });
        }
      });

      nextAnchors.sort((a, b) => a.top - b.top);
      sectionAnchorsRef.current = nextAnchors;
    };

    const scheduleBuild = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(buildAnchors, 80);
    };

    scheduleBuild();
    setTimeout(buildAnchors, 400);
    setTimeout(buildAnchors, 900);

    observer = new MutationObserver(() => scheduleBuild());
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      try {
        observer?.disconnect();
      } catch {
        // ignore
      }
    };
  }, [containerRef, sections, pageScale, zoomLevel]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!numPages) return;

    let rafId = null;
    let lastPage = null;
    let lastSectionIndex = null;

    const computeVisiblePage = () => {
      const containerRect = container.getBoundingClientRect();
      const focusY = containerRect.top + containerRect.height * 0.35;

      const pageElements = container.querySelectorAll('[data-page-number]');
      let bestPage = 1;
      let bestDistance = Number.POSITIVE_INFINITY;

      for (const el of pageElements) {
        const pageAttr = el.getAttribute('data-page-number');
        const pageNum = pageAttr ? parseInt(pageAttr, 10) : Number.NaN;
        if (Number.isNaN(pageNum)) continue;

        const rect = el.getBoundingClientRect();
        const containsFocus = rect.top <= focusY && rect.bottom >= focusY;
        const distance = containsFocus ? 0 : Math.min(Math.abs(rect.top - focusY), Math.abs(rect.bottom - focusY));

        if (distance < bestDistance) {
          bestDistance = distance;
          bestPage = pageNum;
        }
      }

      bestPage = Math.max(1, Math.min(bestPage, numPages || bestPage));
      return bestPage;
    };

    const computeSectionIndexFromAnchors = () => {
      const anchors = sectionAnchorsRef.current || [];
      if (!anchors.length) return null;

      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight || 0;
      const focus = scrollTop + containerHeight * 0.35;

      let chosen = anchors[0].sectionIndex;
      for (const a of anchors) {
        if (a.top <= focus + 1) chosen = a.sectionIndex;
        else break;
      }
      return chosen;
    };

    const computeSectionIndexFromVisiblePage = (visiblePage) => {
      if (!sections || sections.length === 0) return null;
      const pageNum = Math.max(1, parseInt(visiblePage, 10) || 1);

      const normalized = [];
      for (let i = 0; i < sections.length; i += 1) {
        const raw = sections[i]?.pageNumber ?? sections[i]?.page_number;
        const pn = parseInt(raw, 10);
        if (!Number.isFinite(pn) || pn <= 0) continue;
        normalized.push({ originalIndex: i, pageNumber: pn });
      }
      if (!normalized.length) return null;
      normalized.sort((a, b) => a.pageNumber - b.pageNumber);

      let chosenOriginalIndex = normalized[0].originalIndex;
      for (const item of normalized) {
        if (item.pageNumber <= pageNum) chosenOriginalIndex = item.originalIndex;
        else break;
      }
      return chosenOriginalIndex;
    };

    const update = () => {
      if (isProgrammaticScrollRef.current) {
        return;
      }

      const visiblePage = computeVisiblePage();

      const anchorSectionIndex = computeSectionIndexFromAnchors();
      const pageSectionIndex = anchorSectionIndex == null ? computeSectionIndexFromVisiblePage(visiblePage) : null;
      const nextSectionIndex =
        anchorSectionIndex != null
          ? anchorSectionIndex
          : (pageSectionIndex != null ? pageSectionIndex : currentSectionIndex);

      if (nextSectionIndex !== lastSectionIndex) {
        lastSectionIndex = nextSectionIndex;
        setCurrentSectionIndex(nextSectionIndex);
      }

      const nextPage = Math.max(1, Math.min(visiblePage, numPages || visiblePage));

      if (nextPage !== lastPage) {
        lastPage = nextPage;
        setCurrentPage(nextPage);
      }
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      container.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [containerRef, numPages, sections, currentSectionIndex, isProgrammaticScrollRef, setCurrentSectionIndex, setCurrentPage]);

  return {
    goToSection,
    goToNextSection,
    goToPreviousSection,
  };
}
